import { isEmpty } from 'lodash';
import debounce from 'lodash/debounce';
import { PolygonAIActionType, PromptType } from './enums';
import { flattenPredictionWkts } from './helpers';
import { type PolygonAIRequest, type PolygonAIResponse } from './types';

// Constants.
const HOVER_DEBOUNCE_MS = 20;

export class PolygonAISessionController {
  private readonly _sessionId: string;
  private readonly _sendJsonMessage: (payload: PolygonAIRequest) => void;
  private readonly _clear: VoidFunction;
  private readonly _renderPredictions: (
    wktPolygons: string[],
    isHover: boolean,
  ) => void;
  private _hasSelected: boolean = false;
  private _lastActionType: PolygonAIActionType | null = null;

  private readonly _debouncedHover: ((pointWkt: string) => void) & {
    cancel: VoidFunction;
  };

  constructor(
    sessionId: string,
    sendJsonMessage: (payload: PolygonAIRequest) => void,
    clear: VoidFunction,
    renderPredictions: (wktPolygons: string[], isHover: boolean) => void,
  ) {
    this._sessionId = sessionId;
    this._sendJsonMessage = sendJsonMessage;
    this._clear = clear;
    this._renderPredictions = renderPredictions;

    this._debouncedHover = debounce(
      (pointWkt: string) => {
        this._lastActionType = PolygonAIActionType.Hover;
        this._send({
          action_type: PolygonAIActionType.Hover,
          prompt_wkt: pointWkt,
          prompt_type: PromptType.Positive,
        });
      },
      HOVER_DEBOUNCE_MS,
      { leading: false, trailing: true },
    );
  }

  hover(pointWkt: string): void {
    if (this._hasSelected) {
      return;
    }

    this._debouncedHover(pointWkt);
  }

  select(pointWkt: string, promptType: PromptType): void {
    this._debouncedHover.cancel();
    // immediately clear any hover prediction.
    this._renderPredictions([], true);
    this._hasSelected = true;
    this._lastActionType = PolygonAIActionType.Select;
    this._send({
      action_type: PolygonAIActionType.Select,
      prompt_wkt: pointWkt,
      prompt_type: promptType,
    });
  }

  undo(): void {
    this._debouncedHover.cancel();
    this._lastActionType = PolygonAIActionType.Undo;
    this._send({ action_type: PolygonAIActionType.Undo });
  }

  handleResponse(response: PolygonAIResponse): void {
    // Ignore server echo after reset — client already cleared synchronously.
    if (this._lastActionType === PolygonAIActionType.Reset) {
      return;
    }

    const isHover = this._lastActionType === PolygonAIActionType.Hover;
    const hasPredictions = !isEmpty(response.predictions_wkt);

    // Undo is the only action that can transition back to hover mode.
    // If no predictions remain after undo, clear the selected state.
    if (this._lastActionType === PolygonAIActionType.Undo) {
      this._hasSelected = hasPredictions;
    }

    const wktParts = hasPredictions
      ? flattenPredictionWkts(response.predictions_wkt)
      : [];

    this._renderPredictions(wktParts, isHover);
  }

  exitSelectMode(): void {
    this._hasSelected = false;
  }

  hasSelectedPrompt(): boolean {
    return this._hasSelected;
  }

  reset(): void {
    this._debouncedHover.cancel();
    this._hasSelected = false;
    this._lastActionType = PolygonAIActionType.Reset;
    this._clear();
    this._send({ action_type: PolygonAIActionType.Reset });
  }

  destroy(): void {
    this._debouncedHover.cancel();
  }

  private _send(payload: Omit<PolygonAIRequest, 'session_id'>): void {
    this._sendJsonMessage({ session_id: this._sessionId, ...payload });
  }
}
